import { message } from 'antd';
import { useCallback, useState } from 'react';

import {
  addWeeklyUpdate,
  updateWeeklyUpdate,
  type AddWeeklyUpdateRequest,
  ProjectDetailsApiError,
} from '@/api/project-details/api';

export type { AddWeeklyUpdateRequest } from '@/api/project-details/api';
export type { PreviousWeeklyUpdateRecord } from '@/api/project-details/mocks/projectHome.types';

export interface UseWeeklyUpdateResult {
  isSubmitting: boolean;
  submitUpdate: (
    payload: AddWeeklyUpdateRequest,
    mode: 'create' | 'edit' | 'view',
    weekOf?: string,
    onSuccess?: () => void,
  ) => Promise<void>;
}

/**
 * Manages the async submission of a weekly update report.
 * The component is responsible for building the API payload from its local form state;
 * this hook owns the loading state and the network call.
 * mode parameter: determines POST (create) vs PUT (edit)
 */
export function useWeeklyUpdate(
  accessToken: string | null,
  clientId: string,
  projectId: string,
): UseWeeklyUpdateResult {
  const [isSubmitting, setIsSubmitting] = useState(false);

  const submitUpdate = useCallback(
    async (
      payload: AddWeeklyUpdateRequest,
      mode: 'create' | 'edit' | 'view',
      weekOf?: string,
      onSuccess?: () => void,
    ): Promise<void> => {
      if (!accessToken) {
        void message.error('Authentication required. Please log in again.');
        return;
      }
      setIsSubmitting(true);
      try {
        const isUpdate = mode === 'edit';

        if (isUpdate) {
          // Updating existing update
          await updateWeeklyUpdate(accessToken, clientId, projectId, weekOf!, payload);
          void message.success('Weekly update updated successfully!');
        } else {
          // Creating new update (or updating if already exists)
          try {
            await addWeeklyUpdate(accessToken, clientId, projectId, payload);
            void message.success('Weekly update created successfully!');
          } catch (error) {
            // If update already exists for this week, automatically retry with PUT
            if (error instanceof ProjectDetailsApiError && error.statusCode === 409) {
              await updateWeeklyUpdate(accessToken, clientId, projectId, payload.week_of, payload);
              void message.success('Weekly update updated successfully!');
            } else {
              throw error;
            }
          }
        }
        onSuccess?.();
      } catch (error) {
        let errorMessage = 'Failed to save weekly update';

        if (error instanceof ProjectDetailsApiError) {
          if (error.statusCode === 401) {
            errorMessage = 'Session expired. Please log in again to save the weekly update.';
          } else if (error.statusCode === 403) {
            errorMessage = 'You do not have permission to save a weekly update for this project.';
          } else if (error.statusCode !== 409) {
            // Don't show 409 error - it's handled by the fallback
            errorMessage = error.message;
          }
        } else if (error instanceof Error) {
          errorMessage = error.message;
        }

        if (errorMessage) {
          void message.error(errorMessage);
        }
      } finally {
        setIsSubmitting(false);
      }
    },
    [accessToken, clientId, projectId],
  );

  return { isSubmitting, submitUpdate };
}
